/* eslint-disable camelcase */
import KeycloakService from '../../../utils/keycloakService'
import { startSxpProxy } from '../../../utils/api'
import { OPD_PROJECT_ID, RADIOLOGY_PROJECT_ID } from '../../../utils/constants'
import {
  radiologyIntents,
  visit,
  radiologyTestStatus,
  patientText,
  tabs,
  patientIdentifier,
  thopIdText,
  genderConstants,
  mobileText,
  registrationConstants,
  observation,
  radiology,
  radiologyText,
  collectionTable,
} from '../Constants/constants'
import {
  RadiologyOrders,
  RadiologyResult,
  radiologyDepartments,
} from '../models'
import { makeName } from '../../lms/utils'
import { getTotalAge } from '../../../utils/dateUtils'
import { Patient } from 'fhir/r4'
import { orderStatus } from '../../lms/constants'
import { getLocationAddress } from '../../patients/utils'
import { toast } from 'react-toastify'
import { CreateLabInvoice } from '../../../services/LmsService'
import { updateradiologyorderInvoice } from '../../../services/RadiologyService'
import {
  optionValue,
  opd,
  success,
  failedMsg,
  labConstant,
} from '../../labTests/models'
import { OPD_ORDERS_CONSTANTS } from '../../labTests/opdOrdersConstants'

/**
 * Validates if the origin is valid for invoice operations
 */
const isValidInvoiceOrigin = (origin: string): boolean => {
  return (
    origin === optionValue?.b2b?.value ||
    origin?.toLowerCase() === opd?.toLowerCase() ||
    origin === optionValue?.ipd?.value
  )
}

/**
 * Validates the invoice response and handles errors
 */
const validateInvoiceResponse = (
  invoiceResponse: any,
  setSubmitButtonLoading?: (loading: boolean) => void
): { isValid: boolean; response: any } => {
  const result =
    invoiceResponse?.data?.data?.result || invoiceResponse?.data?.result

  if (result?.status !== success) {
    setSubmitButtonLoading?.(false)
    toast.error(result?.message || failedMsg)
    return { isValid: false, response: null }
  }

  return { isValid: true, response: invoiceResponse }
}

/**
 * Updates an existing radiology order invoice
 */
const updateExistingInvoice = async (
  order: any,
  updatedTestIds: any[],
  uhid: string,
  setSubmitButtonLoading?: (loading: boolean) => void
) => {
  const invoiceResponse = await updateradiologyorderInvoice(
    order.origin,
    updatedTestIds,
    Number(order.odoo_invoice_id),
    order.ref_id,
    uhid
  )

  const { isValid, response } = validateInvoiceResponse(
    invoiceResponse,
    setSubmitButtonLoading
  )

  return isValid ? response : null
}

/**
 * Creates a new lab invoice for radiology order
 */
const createNewInvoice = async (
  locationId: any,
  origin: string,
  updatedTestIds: any[],
  uhid: string,
  reqOdooId: any,
  reqDoctorRef: any,
  rows: any[]
) => {
  return await CreateLabInvoice(
    locationId,
    origin,
    updatedTestIds,
    uhid,
    reqOdooId || null,
    reqDoctorRef,
    rows?.[0]?.mode?.value || '',
    true
  )
}

/**
 * Creates or updates a radiology order invoice
 * @param origin - Origin type (B2B or OPD)
 * @param updatedTestIds - Array of test IDs to include in invoice
 * @param order - Existing order object (contains id if updating)
 * @param uhid - Unique Health ID of the patient
 * @param reqOdooId - Odoo request ID
 * @param reqDoctorRef - Doctor reference
 * @param rows - Row data containing mode information
 * @param locationId - Location identifier
 * @param setSubmitButtonLoading - Optional callback to control loading state
 * @returns Invoice response or null if operation fails
 */
export const createOrUpdateInvoice = async (
  origin: string,
  updatedTestIds: any[],
  order: any,
  uhid: string,
  reqOdooId: any,
  reqDoctorRef: any,
  rows: any[],
  locationId: any,
  setSubmitButtonLoading?: (loading: boolean) => void
) => {
  // Validate origin before processing
  if (!isValidInvoiceOrigin(origin)) {
    return null
  }

  // Update existing invoice if order has an ID
  if (order?.id) {
    return await updateExistingInvoice(
      order,
      updatedTestIds,
      uhid,
      setSubmitButtonLoading
    )
  }

  // Create new invoice for new orders
  return await createNewInvoice(
    locationId,
    origin,
    updatedTestIds,
    uhid,
    reqOdooId,
    reqDoctorRef,
    rows
  )
}

export const saveRadiologyOrderFromLab = (
  mode: 'visit' | 'patient' | undefined,
  order: any,
  id: string,
  patientId: string,
  uhid: string,
  lrNumber: string,
  filteredPanels?: any,
  filteredSamples?: [],
  source?: string,
  packageId?: number,
  price?: number,
  requestedBy?: string,
  origin?: string,
  packageIds?: any,
  combinedResult?: any,
  odooInvoiceID?: any,
  refID?: any,
  locationId?: any,
  paymentStatus?: string
) => {
  const intent = order?.id
    ? radiologyIntents?.updateRadiologyOrderById
    : radiologyIntents?.createRadiologyOrder
  const state = {
    radiologyOrderId: order?.id,
    order: order,
    visitId: mode === visit && id ? parseInt(id) : undefined,
    radiologyLabTests: filteredPanels,
    radiologySamples: filteredSamples,
    lrNumber: order?.lr_number ?? lrNumber,
    orderedByName: KeycloakService.getFullname(),
    orderedById: KeycloakService.getSub(),
    patientId: mode === patientText ? id : order?.patient_id ?? patientId,
    patientUhid: uhid ?? order?.patient_uhid ?? undefined,
    status: order?.status ?? radiologyTestStatus?.ordered,
    source: order?.source ?? source ?? undefined,
    packageId: packageId,
    price: price,
    requestedBy: requestedBy,
    origin: origin,
    packageIds: packageIds,
    combinedResult: combinedResult,
    odoo_invoice_id: odooInvoiceID,
    ref_id: refID,
    operating_unit: locationId ?? undefined,
    paymentStatus: paymentStatus ?? order?.payment_status ?? 'unpaid',
  }

  return startSxpProxy(RADIOLOGY_PROJECT_ID, intent, state)
}
export const getDoctorForVisit = (id: string): Promise<string> => {
  const state = { id: id }
  return startSxpProxy(
    OPD_PROJECT_ID,
    radiologyIntents?.getDoctorByVisit,
    state
  )
    .then((doctor) => {
      const { name, qualification } = doctor.data
      return `${name}${qualification ? ` [${qualification}]` : ''}`.trim()
    })
    .catch(() => '')
}
export const TotalPackagePrice = (tests: any[]) => {
  const totalPrice = tests?.reduce((sum, row) => {
    const price = row?.price || 0
    return sum + price
  }, 0)
  return totalPrice
}
export const transformData = (data: any, type = radiologyText) => {
  if (!data?.id) {
    return []
  } else {
    const { radiology_order_tests, origin, requested_by } = data
    const radiologyOrderTests = radiology_order_tests?.filter(
      (e: any) => e?.test_status !== radiologyTestStatus?.deleted
    )
    const testModeResults = radiologyOrderTests
      ?.filter((test: any) => test.is_test_select)
      ?.map((test: any) => ({
        mode: {
          value:
            type === labConstant
              ? radiologyText
              : OPD_ORDERS_CONSTANTS?.MODE_TEST,
          label:
            type === labConstant ? radiology : OPD_ORDERS_CONSTANTS?.LABEL_TEST,
        },
        origin: { value: origin, label: origin },
        requestedBy: { value: requested_by, label: requested_by },
        sampleName: {
          label: test.sample_name,
          value: test.sample_id?.toString(),
        },
        testName: {
          label: test.test_name,
          value: test.test_id,
          odoo_test_id: test?.radiology_test?.odoo_test_id,
        },
        packageName: null,
        packageTests: null,
        price: test?.price || 0,
        discount: test?.discount || 0,
        defaultPrice: test?.default_price || 0,
      }))
    const result = [...(testModeResults ?? [])]
    return result
  }
}
export const TestEnteredFilter = (order: RadiologyOrders | null) => {
  if (order) {
    const filteredTests = order?.radiology_test_results?.filter((test: any) => {
      if (test?.test_status === radiologyTestStatus?.entered) {
        const hasValues = test?.values?.summary
        const referredOutWithDocument = test?.referred_out
          ? !!test?.document_url
          : true

        return hasValues && referredOutWithDocument
      }

      return false
    })

    return filteredTests
  }
}
export const TestSubmittedFilter = (order: RadiologyOrders | null) => {
  if (order) {
    const enteredTests = order?.radiology_order_tests?.filter(
      (test: any) => test?.test_status === radiologyTestStatus?.submitted
    )

    return enteredTests
  }
}
export const filteredTabResults = (
  results: RadiologyResult[],
  activeTab: string
) => {
  let statuses: string[]
  switch (activeTab) {
    case tabs?.testing:
      statuses = [
        radiologyTestStatus?.ordered,
        radiologyTestStatus?.orderModified,
        radiologyTestStatus?.entered,
        radiologyTestStatus?.referredEntered,
        radiologyTestStatus?.reOrdered,
      ]
      break
    case tabs?.validation:
      statuses = [radiologyTestStatus?.submitted]
      break
    default:
      statuses = []
  }

  return results?.filter((result) => statuses?.includes(result?.test_status))
}
export const getPatientIdentifierById = (patient: Patient) => {
  const uhid = patient?.identifier?.find(
    (e: any) => e?.system === patientIdentifier
  )?.value
  return uhid ?? ''
}
export const RegistrationType = (regType: string) => {
  switch (regType) {
    case registrationConstants?.fr:
      return registrationConstants?.full
    case registrationConstants?.qr:
      return registrationConstants?.quick
    case registrationConstants?.erShort:
      return registrationConstants?.er
    case registrationConstants?.pmc:
      return registrationConstants?.pharmacy
    case registrationConstants?.labShort:
      return registrationConstants?.lab
    case registrationConstants?.dgs:
      return registrationConstants?.diagnostics
    case registrationConstants?.camp:
      return registrationConstants?.campaign
    case registrationConstants?.membershipShort:
    case registrationConstants?.membershipPrimary:
      return registrationConstants?.membership
    case registrationConstants?.packageshort:
      return registrationConstants?.package
    default:
      return registrationConstants?.other
  }
}
export const mapPatients = async (
  nodes: RadiologyOrders[]
): Promise<RadiologyOrders[]> => {
  try {
    const nodeIds = nodes?.map((n) => n?.patient_id)
    const ids = Array.from(new Set(nodeIds)).join(',')
    const response = await startSxpProxy(
      OPD_PROJECT_ID,
      radiologyIntents?.getPatientsByIdsApi,
      { ids }
    )
    const entry = response?.data?.entry
    if (entry?.length) {
      const patientObj = entry?.reduce(
        (acc: any, cur: any) => ({ ...acc, [cur?.resource?.id]: cur }),
        {}
      )
      return nodes.map((node) => {
        const pat = node.patient_id ? patientObj[node?.patient_id] : null
        const thopId = pat?.resource?.identifier?.[0]?.value ?? thopIdText
        const gender = pat?.resource?.gender
        const assignedGender =
          gender === genderConstants?.male
            ? genderConstants?.capitalMale
            : gender === genderConstants?.female
            ? genderConstants?.capitalFemale
            : genderConstants?.capitalOthers
        const preferredCommunication = pat?.resoucre?.telecom?.find(
          (e: any) => e?.system === 'sms'
        )
        const preferred =
          preferredCommunication?.value === 'yes' ? 'SMS' : 'WHATSAPP'
        return {
          ...node,
          patientThopId: thopId,
          patientUhid:
            pat?.resource?.identifier?.find(
              (e: any) => e?.system === patientIdentifier
            )?.value ?? thopId,
          patientName: makeName(pat?.resource?.name),
          patientGender: assignedGender,
          patientMobile: pat?.resource?.telecom?.find(
            (prt: any) => prt?.use === mobileText
          )?.value,
          patientAge: getTotalAge(
            new Date(pat?.resource?.birthDate ?? new Date())
          ),
          patientCommunication: preferred,
        }
      })
    }
    return nodes
  } catch (er) {
    return nodes
  }
}
export const mappedPatients = async (nodes: any): Promise<any> => {
  try {
    const nodeIds = nodes?.map((n: any) => n?.patient_registered_id)
    const ids = Array.from(new Set(nodeIds)).join(',')
    const response = await startSxpProxy(
      OPD_PROJECT_ID,
      radiologyIntents?.getPatientsByIdsApi,
      { ids }
    )
    const entry = response?.data?.entry
    if (entry?.length) {
      const patientObj = entry?.reduce(
        (acc: any, cur: any) => ({ ...acc, [cur.resource.id]: cur }),
        {}
      )
      return nodes.map((node: any) => {
        const pat = node?.patient_registered_id
          ? patientObj[node?.patient_registered_id]
          : null
        const gender = pat?.resource?.gender
        const assignedGender =
          gender === genderConstants?.male
            ? genderConstants?.capitalMale
            : gender === genderConstants?.female
            ? genderConstants?.capitalFemale
            : genderConstants?.capitalOthers
        return {
          patientName: makeName(pat?.resource?.name),
          patientGender: assignedGender,
          patientMobile: pat?.resource?.telecom?.find(
            (prt: any) => prt?.use === mobileText
          )?.value,
          patientAge: getTotalAge(
            new Date(pat?.resource?.birthDate ?? new Date())
          ),
          id: node?.id,
          bedId: node?.bed_id,
          bedStatus: node?.bed_status,
          patientId: node?.patient_id,
          dateCreated: node?.date_created,
          dateStarted: node?.date_started,
          dateOfDischarge: node?.date_of_discharge,
          bedNumber: node?.bed.bed_number,
          location: node?.bed.location_id,
          patient_registered_id: node?.patient_registered_id,
          assigned_doctor: node?.assigned_doctor,
          doctor_department: node?.doctor_department,
        }
      })
    }
    return nodes
  } catch (er) {
    return nodes
  }
}
export const removeSampleDuplicates = (arr: any) => {
  const filteredArray = arr?.map((e: any) => e?.sample_name)
  return [...new Set(filteredArray)]
}
export const groupByTestName = (data: any) => {
  const grouped: any = {}

  data?.forEach((item: any) => {
    if (!grouped?.[item?.test_name]) {
      grouped[item?.test_name] = []
    }
    grouped?.[item?.test_name]?.push(item)
  })

  return Object.keys(grouped)?.map((testName) => ({
    testName,
    testStatuses: grouped[testName],
  }))
}
export const transformRadiologyResults = (
  radiologyResults: any[],
  radiologyDepartments: any[]
) => {
  return radiologyResults?.map((e: any) => {
    const department = radiologyDepartments?.find(
      (d: any) => d?.id === e?.authorised_by_id
    )

    return {
      Radiology: [
        {
          id: radiologyText,
          interpretation: e?.radiology_lab_test?.Interpretation ?? null,
          name: e?.test_name,
          sample: e?.sample_name ?? '',
          pathologyName: e?.authorised_by_name,
          pathologySignature: department?.siganture || '',
          pathologistQualification: department?.qualification || '',
          pathologyDepartment: department?.code || '',
          resultType: observation,
          technician_name: e?.validated_by,
          tests: [e],
        },
      ],
    }
  })
}

export const radiologyPrintData = (
  data: any,
  patient: Patient | null,
  locationAddress: any,
  radiologyDepartments: radiologyDepartments[]
) => {
  const item = data?.data?.update_radiology_orders_by_pk
  const radiologyResults = item?.radiology_test_results?.filter(
    (e: any) => e?.test_status === orderStatus?.approved
  )
  const refractored = radiologyResults?.map((e: any) => {
    const obj = {
      id: radiology,
      interpretation: e?.radiology_lab_test?.Interpretation ?? null,
      name: e?.test_name,
      sample: e?.sample_name ?? '',
      pathologyName: e?.authorised_by_name,
      pathologySignature:
        radiologyDepartments?.find((d: any) => d?.id === e?.authorised_by_id)
          ?.siganture || '',
      pathologistQualification:
        radiologyDepartments?.find((d: any) => d?.id === e?.authorised_by_id)
          ?.qualification || '',
      pathologyDepartment:
        radiologyDepartments?.find((d: any) => d?.id === e?.authorised_by_id)
          ?.code || '',
      resultType: observation,
      technician_name: e?.validated_by,
      tests: [e],
    }
    return { Radiology: [obj] }
  })

  const obj: any = {
    type: 'radiology',
    id: patient?.id,
    name: makeName(patient?.name),
    age: getTotalAge(new Date(patient?.birthDate ?? new Date())),
    gender:
      patient?.gender === genderConstants?.male
        ? genderConstants?.capitalMale
        : patient?.gender === genderConstants?.female
        ? genderConstants?.capitalFemale
        : genderConstants?.capitalOthers,
    patientAddress: locationAddress,
    mobile: patient?.telecom?.find((prt: any) => prt?.use === mobileText)
      ?.value,
    lrNumber: item?.lr_number ?? '',
    radiologyTests: item?.radiology_order_tests,
    radiologyResults: refractored,
    orderType: item?.origin ?? '',
    orderedOn: item?.ordered_on ?? '',
    invoiceWithHeaderValue: true,
    requestedBy: item?.requested_by ?? item?.ordered_by_name ?? '',
    validatedOn: item?.validation_completed_on ?? '',
    fileId: item?.document_id,
  }

  return obj
}
export const radiologyuploadToDMS = async (
  blobData: Blob,
  patient: any,
  locationAddress: any
) => {
  const name = makeName(patient?.name)
  const mobile = patient?.telecom?.find(
    (prt: any) => prt?.use === mobileText
  )?.value
  try {
    const myHeaders = new Headers()
    myHeaders.append(
      'Authorization',
      `Bearer ${sessionStorage.getItem('react-token')}`
    )

    const formdata = new FormData()
    formdata.append('stateId', '1971')
    formdata.append('preference', 'WHATSAPP')
    formdata.append('sendTo', `91${mobile}`)
    formdata.append('location', `${locationAddress}`)
    formdata.append('name', `${name}`)
    formdata.append('sourceType', 'radiology')
    formdata.append('file', blobData, 'test_pdf.pdf')
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow',
    }

    const response = await fetch(
      radiologyIntents?.notificationUpload,
      requestOptions
    )

    if (response?.ok) {
      const result = await response.json()
      return result
    } else {
      return { fileId: '' }
    }
  } catch (error) {
    console.error(error)
  }
}

export const prepareRadiologyData = async (
  params: any,
  location: any,
  locations: any,
  radiologyDepartments: radiologyDepartments[],
  radiologyText: string,
  prefix: any,
  generatePDF: (data: any) => Promise<Blob>
): Promise<Blob | undefined> => {
  try {
    const item = params?.data
    const prefixIdentifier = location?.resource?.identifier?.find(
      (item: any) => item?.system === prefix
    )
    const locationAddress = getLocationAddress(
      prefixIdentifier?.value || '',
      locations
    )
    const refractored = item?.radiology_test_results?.map((e: any) => {
      const obj = {
        id: radiologyText,
        interpretation: e?.radiology_lab_test?.Interpretation ?? null,
        name: e?.test_name,
        sample: e?.sample_name,
        pathologyName: e?.authorised_by_name,
        pathologySignature:
          radiologyDepartments?.find((d) => d?.id === e?.authorised_by_id)
            ?.siganture || '',
        pathologistQualification:
          radiologyDepartments?.find((d) => d?.id === e?.authorised_by_id)
            ?.qualification || '',
        pathologyDepartment: 'radiology',
        resultType: 'observation',
        technician_name: e?.validated_by,
        tests: [e],
      }
      return { Radiology: [obj] }
    })
    const obj: any = {
      type: radiologyText,
      id:
        item?.patientUhid ?? item?.patientThopId ?? item?.patient_id ?? prefix,
      name: item?.patientName,
      age: item?.patientAge,
      gender: item?.patientGender,
      patientAddress: locationAddress,
      mobile: item?.patientMobile ?? '',
      lrNumber: item?.lr_number ?? '',
      radiologyTests: item?.radiology_order_tests,
      radiologyResults: refractored,
      orderType: item?.origin ?? '',
      orderedOn: item?.ordered_on ?? '',
      invoiceWithHeaderValue: false,
      requestedBy: item?.requested_by ?? item?.ordered_by_name ?? '',
      validatedOn: item?.validation_completed_on ?? '',
      fileId: item?.document_id,
    }
    const pdfBlob = await generatePDF(obj)
    return pdfBlob
  } catch (err) {
    console.error(err)
    toast?.error(collectionTable?.InoviceError)
    return undefined
  }
}
export const radiologyStatusToTab = (statuses: string[]): string[] => {
  const statusMap: Record<string, string> = {
    [orderStatus?.ordered]: orderStatus?.testing,
    [orderStatus?.orderedModified]: orderStatus?.testing,
    [orderStatus?.collected]: orderStatus?.testing,
    [orderStatus?.collectionRejected]: orderStatus?.testing,
    [orderStatus?.rejected]: orderStatus?.testing,
    [orderStatus?.collectionApproved]: orderStatus?.testing,
    [orderStatus?.entered]: orderStatus?.testing,
    [orderStatus?.enteredReferred]: orderStatus?.testing,
    [orderStatus?.submitted]: orderStatus?.validation,
    [orderStatus?.approved]: orderStatus?.Dispatch,
  }

  const result = statuses
    ?.map((status) => statusMap[status]) // Map statuses to their corresponding tabs
    .filter((tab) => tab) // Remove undefined values for unmatched statuses

  return result
}
